import { registerMessages } from "../../../i18n/runtime";

export const stockCostingEn = {
  "pos.costing.title": "Stock costs and opening balances",
  "pos.costing.subtitle": "Review native stock value, establish opening quantities, and control stock deductions from sales.",
  "pos.costing.back": "Stock receiving",
  "pos.costing.scope": "Opening balances add native stock with recorded source evidence. Archived items stay archived. Stock tools do not activate prices, printing or payables. Managed costing-to-GL requires its own reviewed mappings, historical reconciliation and posting approvals.",
  "pos.costing.valuation": "Stock valuation",
  "pos.costing.openings": "Opening balances",
  "pos.costing.mappings": "Sales stock mappings",
  "pos.costing.location": "Stock location",
  "pos.costing.chooseLocation": "Choose a location",
  "pos.costing.item": "Item / variant",
  "pos.costing.sku": "SKU",
  "pos.costing.quantity": "Quantity",
  "pos.costing.unitCost": "Unit cost",
  "pos.costing.totalCost": "Recorded cost",
  "pos.costing.knownQuantity": "Quantity with known cost",
  "pos.costing.unknownQuantity": "Quantity without cost",
  "pos.costing.method": "Cost method",
  "pos.costing.fifo": "FIFO",
  "pos.costing.unknownCost": "Unknown cost",
  "pos.costing.valuationNote": "FIFO uses the oldest available cost layers first. Quantities without recorded cost are shown separately; unknown cost is never treated as zero.",
  "pos.costing.chooseValuation": "Choose a location to review its stock quantities and recorded costs.",
  "pos.costing.noValuation": "No stock valuation rows at this location.",
  "pos.costing.readOnly": "You can review stock. A POS manager must post opening balances or change sales stock mappings.",
  "pos.costing.newOpening": "Prepare an opening balance",
  "pos.costing.steps": "1. Record the source and quantities. 2. Review the server preview. 3. Confirm and post.",
  "pos.costing.details": "1. Opening balance details",
  "pos.costing.effectiveOn": "Effective date",
  "pos.costing.provenance": "Source evidence",
  "pos.costing.provenanceHint": "Identify the approved count, source export, or reconciliation that supports these quantities and costs.",
  "pos.costing.reason": "Reason for opening balance",
  "pos.costing.findVariant": "Find an item, variant, or SKU",
  "pos.costing.searchHint": "Enter at least two characters. Archived items are included for stock reconciliation.",
  "pos.costing.searching": "Searching…",
  "pos.costing.addVariant": "Add {name}",
  "pos.costing.archived": " · Archived",
  "pos.costing.noVariants": "No matching variants.",
  "pos.costing.narrow": "More results exist. Narrow your search.",
  "pos.costing.lines": "Opening quantities and costs",
  "pos.costing.quantityLine": "Quantity, line {line}",
  "pos.costing.costLine": "Unit cost, line {line}",
  "pos.costing.decimalHint": "Use exact decimal values, with up to four decimal places. Quantities and costs must be nonnegative. Any zero value must be supported by the source evidence.",
  "pos.costing.remove": "Remove line {line}",
  "pos.costing.preview": "Review opening balance",
  "pos.costing.review": "2. Review opening balance",
  "pos.costing.previewNote": "Confirm every variant, quantity, cost, location, effective date, and source before posting. Any edit requires a new preview.",
  "pos.costing.acknowledge": "I have reconciled this opening balance to the source evidence and authorize adding this stock.",
  "pos.costing.immutable": "Posting creates a permanent opening record. Repeating this request will not post it twice.",
  "pos.costing.post": "3. Confirm and post opening balance",
  "pos.costing.posted": "Opening balance posted",
  "pos.costing.postedDetail": "Record {id} · Posted {at}",
  "pos.costing.startNew": "Prepare another opening balance",
  "pos.costing.stale": "The stock or preview changed. Review a fresh preview and confirm it again before posting.",
  "pos.costing.history": "Posted opening balances",
  "pos.costing.noOpenings": "No opening balances have been posted.",
  "pos.costing.mappingNote": "A mapping connects one sales outlet and item variant to a stock location. Mappings are saved disabled unless you explicitly enable stock deductions. They do not activate archived items or sales printing.",
  "pos.costing.configureMapping": "Configure a sales stock mapping",
  "pos.costing.outlet": "Sales outlet",
  "pos.costing.chooseOutlet": "Choose a sales outlet",
  "pos.costing.selectedVariant": "Selected variant",
  "pos.costing.chooseVariant": "Search above and select a variant",
  "pos.costing.selectVariant": "Select {name}",
  "pos.costing.enable": "Enable stock deductions for this mapping",
  "pos.costing.enableAcknowledge": "I authorize posted sales at this outlet to deduct this variant from the selected stock location.",
  "pos.costing.saveMapping": "Save mapping",
  "pos.costing.mappingSaved": "Sales stock mapping saved.",
  "pos.costing.mappingHistory": "Configured mappings",
  "pos.costing.noMappings": "No sales stock mappings are configured.",
  "pos.costing.status": "Status",
  "pos.costing.enabled": "Enabled",
  "pos.costing.disabled": "Disabled",
  "pos.costing.editMapping": "Review mapping",
  "pos.costing.loadMore": "Show more",
} as const;
export type StockCostingMessages = typeof stockCostingEn;
registerMessages("en", stockCostingEn);

registerMessages("es", {
  "pos.costing.title": "Costos de existencias y saldos iniciales",
  "pos.costing.subtitle": "Revise el valor de las existencias, establezca cantidades iniciales y controle las deducciones por ventas.",
  "pos.costing.back": "Recepción de existencias",
  "pos.costing.scope": "Los saldos iniciales agregan existencias con evidencia de origen. Los artículos archivados siguen archivados. Las herramientas de existencias no activan precios, impresión ni cuentas por pagar. La contabilidad de costos exige sus propias cuentas revisadas, conciliación histórica y aprobaciones de contabilización.",
  "pos.costing.valuation": "Valoración de existencias",
  "pos.costing.openings": "Saldos iniciales",
  "pos.costing.mappings": "Asignaciones de existencias para ventas",
  "pos.costing.location": "Ubicación de existencias",
  "pos.costing.chooseLocation": "Elija una ubicación",
  "pos.costing.item": "Artículo / variante",
  "pos.costing.sku": "SKU",
  "pos.costing.quantity": "Cantidad",
  "pos.costing.unitCost": "Costo unitario",
  "pos.costing.totalCost": "Costo registrado",
  "pos.costing.knownQuantity": "Cantidad con costo conocido",
  "pos.costing.unknownQuantity": "Cantidad sin costo",
  "pos.costing.method": "Método de costo",
  "pos.costing.fifo": "FIFO",
  "pos.costing.unknownCost": "Costo desconocido",
  "pos.costing.valuationNote": "FIFO usa primero las capas de costo más antiguas disponibles. Las cantidades sin costo registrado se muestran por separado; un costo desconocido nunca se considera cero.",
  "pos.costing.chooseValuation": "Elija una ubicación para revisar sus cantidades y costos registrados.",
  "pos.costing.noValuation": "No hay filas de valoración en esta ubicación.",
  "pos.costing.readOnly": "Puede revisar las existencias. Un gerente de POS debe registrar saldos iniciales o cambiar las asignaciones para ventas.",
  "pos.costing.newOpening": "Preparar un saldo inicial",
  "pos.costing.steps": "1. Registre el origen y las cantidades. 2. Revise la vista previa del servidor. 3. Confirme y registre.",
  "pos.costing.details": "1. Detalles del saldo inicial",
  "pos.costing.effectiveOn": "Fecha efectiva",
  "pos.costing.provenance": "Evidencia de origen",
  "pos.costing.provenanceHint": "Identifique el conteo aprobado, la exportación o la conciliación que respalda estas cantidades y costos.",
  "pos.costing.reason": "Motivo del saldo inicial",
  "pos.costing.findVariant": "Buscar artículo, variante o SKU",
  "pos.costing.searchHint": "Escriba al menos dos caracteres. Se incluyen artículos archivados para conciliar existencias.",
  "pos.costing.searching": "Buscando…",
  "pos.costing.addVariant": "Agregar {name}",
  "pos.costing.archived": " · Archivado",
  "pos.costing.noVariants": "No hay variantes coincidentes.",
  "pos.costing.narrow": "Hay más resultados. Afine la búsqueda.",
  "pos.costing.lines": "Cantidades y costos iniciales",
  "pos.costing.quantityLine": "Cantidad, línea {line}",
  "pos.costing.costLine": "Costo unitario, línea {line}",
  "pos.costing.decimalHint": "Use valores decimales exactos, con hasta cuatro decimales. La cantidad y el costo no pueden ser negativos. Cualquier valor cero requiere evidencia de origen.",
  "pos.costing.remove": "Quitar línea {line}",
  "pos.costing.preview": "Revisar saldo inicial",
  "pos.costing.review": "2. Revisar saldo inicial",
  "pos.costing.previewNote": "Confirme cada variante, cantidad, costo, ubicación, fecha y origen antes de registrar. Todo cambio requiere una nueva vista previa.",
  "pos.costing.acknowledge": "He conciliado este saldo inicial con la evidencia de origen y autorizo agregar estas existencias.",
  "pos.costing.immutable": "Se creará un registro inicial permanente. Repetir esta solicitud no lo registrará dos veces.",
  "pos.costing.post": "3. Confirmar y registrar saldo inicial",
  "pos.costing.posted": "Saldo inicial registrado",
  "pos.costing.postedDetail": "Registro {id} · Registrado {at}",
  "pos.costing.startNew": "Preparar otro saldo inicial",
  "pos.costing.stale": "Las existencias o la vista previa cambiaron. Revise una nueva vista previa y vuelva a confirmar antes de registrar.",
  "pos.costing.history": "Saldos iniciales registrados",
  "pos.costing.noOpenings": "No se han registrado saldos iniciales.",
  "pos.costing.mappingNote": "Una asignación conecta un punto de venta y una variante con una ubicación de existencias. Se guarda deshabilitada a menos que active las deducciones expresamente. No activa artículos archivados ni la impresión de ventas.",
  "pos.costing.configureMapping": "Configurar una asignación para ventas",
  "pos.costing.outlet": "Punto de venta",
  "pos.costing.chooseOutlet": "Elija un punto de venta",
  "pos.costing.selectedVariant": "Variante seleccionada",
  "pos.costing.chooseVariant": "Busque arriba y seleccione una variante",
  "pos.costing.selectVariant": "Seleccionar {name}",
  "pos.costing.enable": "Activar deducciones de existencias para esta asignación",
  "pos.costing.enableAcknowledge": "Autorizo que las ventas registradas en este punto deduzcan esta variante de la ubicación seleccionada.",
  "pos.costing.saveMapping": "Guardar asignación",
  "pos.costing.mappingSaved": "Asignación guardada.",
  "pos.costing.mappingHistory": "Asignaciones configuradas",
  "pos.costing.noMappings": "No hay asignaciones de existencias para ventas.",
  "pos.costing.status": "Estado",
  "pos.costing.enabled": "Activada",
  "pos.costing.disabled": "Desactivada",
  "pos.costing.editMapping": "Revisar asignación",
  "pos.costing.loadMore": "Mostrar más",
});
